import React, { useEffect, useRef, useState } from 'react';
import * as tf from '@tensorflow/tfjs';
import './App.css';

const App = () => {
    const [model, setModel] = useState(null);
    const [label, setLabel] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [cameraError, setCameraError] = useState(''); 

    useEffect(() => {
        const loadModel = async () => {
            await tf.ready();
            try {
                const loadedModel = await tf.loadLayersModel('assets/model.json');
                setModel(loadedModel);
            } catch (error) {
                console.error('Error loading model:', error);
            }
        };

        loadModel();
    }, []);

    useEffect(() => {
        const startCamera = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter(device => device.kind === 'videoinput');
                const rearCamera = videoDevices.find(device => device.label.toLowerCase().includes('back') || device.label.toLowerCase().includes('rear'));
                const cameraToUse = rearCamera || videoDevices[0];
                if (!cameraToUse) throw new Error('No camera found.');
                const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: cameraToUse.deviceId } } });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    setCameraError('');
                }
            } catch (error) {
                console.error('Error accessing camera:', error);
                setCameraError('Could not access the camera. Please check your browser settings.');
            }
        };

        if (isScanning) startCamera();
        else {
            if (videoRef.current && videoRef.current.srcObject) {
                const stream = videoRef.current.srcObject;
                const tracks = stream.getTracks();
                tracks.forEach(track => track.stop());
                videoRef.current.srcObject = null;
            }
        }
    }, [isScanning]);

    const processFrame = async () => {
        if (!model || isProcessing) return;

        setIsProcessing(true);
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const width = 224;
        const height = 224;
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(video, 0, 0, width, height);

        const tensorImg = tf.browser.fromPixels(canvas).expandDims(0).div(255);
        const preds = await model.predict(tensorImg).data();
        processPredictions(preds);
        setIsProcessing(false);
    };

    const processPredictions = (predictions) => {
        if (!predictions || predictions.length === 0) return;

        const good = predictions[1] * 100;
        const bad = predictions[0] * 100;

        if (good > bad) {
            setLabel(`Good Cassette: ${good.toFixed(2)}%`);
        } else {
            setLabel(`Faulty Cassette: ${bad.toFixed(2)}%`);
        }
    };

    const toggleScanning = () => {
        setIsScanning(prev => !prev);
        setLabel('');
    };

    useEffect(() => {
        let interval;
        if (isScanning) {
            interval = setInterval(() => {
                processFrame();
            }, 1000);
        } else {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isScanning, model]);

    return (
        <div className="app-container">
            <h1 className="app-title">Biovue Cassettes Fault Detection</h1>
            <div className="buttons-container">
                <button className="scan-button" onClick={toggleScanning}>
                    {isScanning ? 'Stop Scanning' : 'Start Scanning'}
                </button>
            </div>
            {cameraError && <div className="camera-error">{cameraError}</div>}
            {isScanning && !cameraError && (
                <div className="video-container">
                    <video
                        ref={videoRef}
                        autoPlay
                        muted
                        className="video-feed"
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
                </div>
            )}
            {label && (
                <div className="result-container">
                    <div className={`result-label ${label.startsWith('Faulty') ? 'faulty' : 'good'}`}>{label}</div>
                    <div className="progress-bar-container">
                        <div
                            className="progress-bar"
                            style={{
                                width: `${label.includes('Good') ? parseFloat(label.split(':')[1]) : 0}%`,
                                backgroundColor: '#4CAF50',
                            }}
                        >
                            <div className="percentage-text">
                                {label.includes('Good') ? label.split(':')[1] : 0}%
                            </div>
                        </div>
                    </div>
                    <div className="progress-bar-container">
                        <div
                            className="progress-bar"
                            style={{
                                width: `${label.includes('Faulty') ? parseFloat(label.split(':')[1]) : 0}%`,
                                backgroundColor: '#F44336',
                            }}
                        >
                            <div className="percentage-text">
                                {label.includes('Faulty') ? label.split(':')[1] : 0}%
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default App;
